'use client';

import { useState } from 'react';

interface FAQItem {
  question: string;
  answer: string;
}

const faqs: FAQItem[] = [
  {
    question: 'Quali sono i tempi di consegna per un ordine di stampa 3D?',
    answer: 'I tempi di consegna variano in base alla complessità del progetto e alla tecnologia utilizzata. In media, per prototipi semplici consegniamo in 3-5 giorni lavorativi. Per progetti più complessi o produzioni in serie, i tempi possono estendersi a 7-14 giorni. Offriamo anche un servizio express per urgenze.',
  },
  {
    question: 'Che formati di file accettate per la stampa?',
    answer: 'Accettiamo i formati più comuni per la stampa 3D: STL, OBJ, 3MF e STEP. Se hai il tuo progetto in un formato diverso, contattaci e troveremo una soluzione. Offriamo anche servizi di conversione e ottimizzazione dei file per la stampa.',
  },
  {
    question: 'Qual è la differenza tra FDM, SLA e SLS?',
    answer: 'FDM (Fused Deposition Modeling) deposita strati di filamento fuso, ideale per prototipi funzionali e parti resistenti. SLA (Stereolitografia) usa resina fotopolimerica per dettagli molto fini e finiture lisce. SLS (Sinterizzazione Laser Selettiva) fonde polvere di nylon con un laser, perfetto per parti meccaniche resistenti e geometrie complesse.',
  },
  {
    question: 'Posso richiedere un preventivo senza impegno?',
    answer: 'Assolutamente sì! Puoi caricare il tuo file 3D sulla nostra piattaforma e ricevere un preventivo istantaneo. In alternativa, contattaci via email o telefono per discutere del tuo progetto. I nostri preventivi sono sempre gratuiti e senza impegno.',
  },
  {
    question: 'Offrite servizi di progettazione CAD?',
    answer: 'Sì, offriamo un servizio completo di progettazione CAD. Possiamo partire da schizzi, disegni tecnici, foto o anche solo da una descrizione della tua idea. Il nostro team di progettisti creerà il modello 3D ottimizzato per la stampa.',
  },
  {
    question: 'Quali dimensioni massime potete stampare?',
    answer: 'Le dimensioni massime dipendono dalla tecnologia scelta. Con FDM possiamo stampare pezzi fino a 300x300x400mm. Per SLA arriviamo a 145x145x175mm con altissima precisione. Con SLS possiamo realizzare pezzi fino a 260x260x450mm. Per oggetti più grandi, possiamo valutare la stampa in più parti.',
  },
];

export default function FAQ() {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  const toggleFAQ = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section id="faq" className="py-24 bg-white">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section header */}
        <div className="text-center mb-16">
          <h2 className="section-title font-display">
            Domande <span className="text-gradient">frequenti</span>
          </h2>
          <p className="section-subtitle mx-auto">
            Trova le risposte alle domande più comuni sui nostri servizi di stampa 3D.
          </p>
        </div>

        {/* FAQ Accordion */}
        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <div
              key={index}
              className={`
                bg-white rounded-2xl border transition-all duration-300
                ${openIndex === index 
                  ? 'border-primary-200 shadow-lg shadow-primary-500/10' 
                  : 'border-secondary-100 hover:border-secondary-200'
                }
              `}
            >
              {/* Question */}
              <button
                onClick={() => toggleFAQ(index)}
                className="w-full flex items-center justify-between gap-4 p-6 text-left"
              >
                <span className={`
                  font-semibold transition-colors duration-200
                  ${openIndex === index ? 'text-primary-600' : 'text-secondary-500'}
                `}>
                  {faq.question}
                </span>
                <span className={`
                  flex-shrink-0 w-10 h-10 rounded-xl flex items-center justify-center
                  transition-all duration-300
                  ${openIndex === index 
                    ? 'bg-primary-600 text-white rotate-180' 
                    : 'bg-secondary-100 text-secondary-400'
                  }
                `}>
                  <svg 
                    className="w-5 h-5" 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path 
                      strokeLinecap="round" 
                      strokeLinejoin="round" 
                      strokeWidth={2} 
                      d="M19 9l-7 7-7-7" 
                    />
                  </svg>
                </span>
              </button>

              {/* Answer */}
              <div className={`
                overflow-hidden transition-all duration-300 ease-in-out
                ${openIndex === index ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}
              `}>
                <div className="px-6 pb-6 pt-0">
                  <p className="text-secondary-400 leading-relaxed">
                    {faq.answer}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="text-center mt-12 p-8 bg-secondary-50 rounded-2xl">
          <p className="text-secondary-500 font-medium mb-2">
            Non hai trovato la risposta che cercavi?
          </p>
          <p className="text-secondary-400 text-sm mb-4">
            Contattaci direttamente, siamo qui per aiutarti.
          </p>
          <a 
            href="/contatti"
            className="inline-flex items-center gap-2 text-primary-700 font-semibold hover:text-primary-800 transition-colors"
          >
            Contattaci
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
            </svg>
          </a>
        </div>
      </div>
    </section>
  );
}

